import { useState } from 'react'

interface CSVPreviewRow {
  line_number: number
  employee_number: string | null
  full_name: string
  email: string
  role: string
  errors: string[]
  formula_injection_flag: boolean
  action: string
}

interface CSVPreview {
  total_rows: number
  valid_rows: number
  error_rows: number
  rows: CSVPreviewRow[]
  detected_encoding: string
  detected_delimiter: string
}

interface CSVCommit {
  summary: { created: number; skipped_existing: number; errors: number }
  created_user_ids: string[]
}

interface Props {
  onClose: () => void
  onImported: () => void
}

export function CSVImportDialog({ onClose, onImported }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<CSVPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [commitResult, setCommitResult] = useState<CSVCommit | null>(null)

  async function doPreview() {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/admin/users/csv-import/preview', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      })
      if (!r.ok) throw new Error('preview failed')
      setPreview((await r.json()) as CSVPreview)
    } catch {
      setError('Не удалось разобрать файл')
    } finally {
      setBusy(false)
    }
  }

  async function doCommit() {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const r = await fetch('/api/admin/users/csv-import/commit', {
        method: 'POST',
        body: fd,
        credentials: 'same-origin',
      })
      if (!r.ok) throw new Error('commit failed')
      setCommitResult((await r.json()) as CSVCommit)
    } catch {
      setError('Импорт не удался')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4">
          Импорт пользователей из CSV
        </h2>

        {!commitResult ? (
          <>
            <div className="space-y-3 mb-4">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block text-sm"
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                onClick={() => void doPreview()}
                disabled={!file || busy}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
              >
                {busy ? 'Анализирую…' : 'Предпросмотр'}
              </button>
            </div>

            {preview && (
              <>
                <div className="mb-3 text-sm text-zinc-600 dark:text-zinc-400">
                  Всего строк: {preview.total_rows} ·{' '}
                  <span className="text-green-600">валидных: {preview.valid_rows}</span>{' '}
                  ·{' '}
                  <span className="text-red-600">с ошибками: {preview.error_rows}</span>{' '}
                  · кодировка: {preview.detected_encoding} · разделитель:{' '}
                  <code>{preview.detected_delimiter}</code>
                </div>
                <div className="border border-zinc-200 dark:border-zinc-700 rounded-md overflow-hidden mb-4">
                  <table className="w-full text-xs">
                    <thead className="bg-zinc-50 dark:bg-zinc-900">
                      <tr className="text-left text-zinc-600 dark:text-zinc-400">
                        <th className="px-2 py-1">Стр.</th>
                        <th className="px-2 py-1">ФИО</th>
                        <th className="px-2 py-1">Email</th>
                        <th className="px-2 py-1">Роль</th>
                        <th className="px-2 py-1">Действие</th>
                        <th className="px-2 py-1">Ошибки</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                      {preview.rows.slice(0, 50).map((r) => (
                        <tr key={r.line_number}>
                          <td className="px-2 py-1">{r.line_number}</td>
                          <td className="px-2 py-1">{r.full_name}</td>
                          <td className="px-2 py-1">{r.email}</td>
                          <td className="px-2 py-1">{r.role}</td>
                          <td className="px-2 py-1">
                            {r.action === 'create' && (
                              <span className="text-green-600">создать</span>
                            )}
                            {r.action === 'update_existing' && (
                              <span className="text-amber-600">обновить</span>
                            )}
                            {r.action === 'error' && (
                              <span className="text-red-600">ошибка</span>
                            )}
                            {r.formula_injection_flag && (
                              <span className="ml-2 text-orange-600" title="formula injection">
                                ⚠
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-red-600">
                            {r.errors.join('; ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400"
                  >
                    Отмена
                  </button>
                  <button
                    onClick={() => void doCommit()}
                    disabled={busy || preview.valid_rows === 0}
                    className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
                  >
                    {busy ? 'Импортирую…' : `Импортировать ${preview.valid_rows} строк`}
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="space-y-4">
            <p className="text-sm">
              <span className="text-green-600">Создано: {commitResult.summary.created}</span>{' '}
              ·{' '}
              <span className="text-amber-600">
                пропущено: {commitResult.summary.skipped_existing}
              </span>{' '}
              ·{' '}
              <span className="text-red-600">ошибок: {commitResult.summary.errors}</span>
            </p>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Передайте каждому импортированному пользователю команду «Сбросить пароль»
              из таблицы — Phase 7 заменит это magic-link через SMTP.
            </p>
            <div className="flex justify-end">
              <button
                onClick={onImported}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm"
              >
                Готово
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
